'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AddressInputProps } from './AddressInput.types';
import Script from 'next/script';
import styles from './AddressInput.module.css';
import { Flex, Text } from '@/_components';

const ADDRESS_COMPONENTS: { [key: string]: string } = {
  subpremise: 'short_name',
  street_number: 'short_name',
  route: 'long_name',
  locality: 'long_name',
  administrative_area_level_1: 'short_name',
  administrative_area_level_2: 'long_name',
  country: 'short_name',
  postal_code: 'short_name',
};

const BASIC_DATA_FIELDS = [
  'address_components',
  'adr_address',
  'alt_id',
  'formatted_address',
  'geometry',
  'icon',
  'id',
  'name',
  'business_status',
  'photo',
  'place_id',
  'scope',
  'type',
  'url',
  'utc_offset_minutes',
  'vicinity',
];

export const AddressInput: React.FC<AddressInputProps> = ({
  id,
  placeholder = 'Start typing',
  disabled = false,
  fields = BASIC_DATA_FIELDS,
  initialValue,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState<string>(initialValue || '');
  const [isLoaded, setLoaded] = useState<boolean>(false);

  const autocompleteRef = useRef<HTMLInputElement>(null);
  const autocomplete = useRef<any>(null);

  const handlePlaceChanged = useCallback(() => {
    const place = autocomplete.current.getPlace();

    if (place.geometry) {
      const formattedPlace = formatResult(place);

      onChange(formattedPlace);
      setInputValue(place.formatted_address || '');
    }
  }, [onChange]);

  const initAutocomplete = useCallback(() => {
    if (autocompleteRef.current) {
      autocomplete.current = new window.google.maps.places.Autocomplete(
        autocompleteRef.current,
      );
      autocomplete.current.setFields(fields);
      autocomplete.current.addListener('place_changed', handlePlaceChanged);
    }
  }, [fields, handlePlaceChanged]);

  useEffect(() => {
    if (isLoaded) {
      initAutocomplete();
    }
  }, [isLoaded, initAutocomplete]);

  const formatResult = (place: {
    address_components: Array<{ types: string }>;
    geometry: {
      location: {
        lat: Function;
        lng: Function;
      };
    };
  }) => {
    const returnData: any = {};

    place.address_components.forEach((component: any) => {
      const addressType = component.types[0];
      const attribute = ADDRESS_COMPONENTS[addressType];
      if (attribute) {
        returnData[addressType] = component[attribute];
      }
    });

    if (place.geometry?.location) {
      returnData.latitude = place.geometry.location.lat();
      returnData.longitude = place.geometry.location.lng();
    }

    return returnData;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  return (
    <>
      <Script
        src={`https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY}&libraries=places`}
        onLoad={() => setLoaded(true)}
      />
      <Flex gap={2} isColumn={true} className={styles.addressInputContainer}>
        <label htmlFor={id}>
          <Text type="small" fontWeight="medium">
            Address
          </Text>
        </label>
        <input
          ref={autocompleteRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          id={id}
          className={styles.addressInput}
        />
      </Flex>
    </>
  );
};
